import CopyableId from "@/components/common/CopyableId";
import { RingGauge } from "@/components/common/RingGauge";
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Users, UserPlus, UserMinus, UserCheck, BellRing, Video, MapPin, Phone, ShieldCheck,
  ShieldAlert, AlertTriangle, Activity, HeartPulse, FileBarChart, FileDown,
  ChevronRight, Search, Filter, RefreshCw, ClipboardList, Pill, FlaskConical,
  Home as HomeIcon, TrendingUp, TrendingDown, ArrowUpRight, RotateCcw, Layers,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, PieChart, Pie, Cell,
  LineChart, Line, CartesianGrid, Legend, AreaChart, Area,
} from "recharts";
import PageHeader from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { RISK_COLORS, NEWS2_COLORS, SOURCE_COLORS } from "@/pages/command/constants";
import "@/pages/command/command.css";

function pct(part, total) { return total ? Math.round((part / total) * 1000) / 10 : 0; }

function timeAgo(iso) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60000) return "just now";
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr ago`;
  return `${Math.floor(h / 24)} d ago`;
}

function Trend({ value, suffix = "%" }) {
  if (value == null) return null;
  if (value > 0) return <span className="cc-trend-up"><TrendingUp className="w-3 h-3 inline" /> {value}{suffix}</span>;
  if (value < 0) return <span className="cc-trend-down"><TrendingDown className="w-3 h-3 inline" /> {value}{suffix}</span>;
  return <span className="cc-trend-flat">— 0{suffix}</span>;
}

function StatTile({ icon: Icon, accent = "#0B7E8C", label, value, trend, spark, onClick, testId, caption }) {
  return (
    <button onClick={onClick} disabled={!onClick} className={`cc-tile ${onClick ? "clickable" : ""}`} data-testid={testId}>
      <span className="cc-tile-icon" style={{ background: `${accent}15`, color: accent }}>
        <Icon className="w-4 h-4" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="cc-tile-label">{label}</div>
        <div className="cc-tile-value">{value ?? "—"}</div>
        {caption && (
          <div className="text-[10px] text-[#94A3B8] mt-0.5 leading-tight" data-testid={testId ? `${testId}-caption` : undefined}>
            {caption}
          </div>
        )}
        {trend != null && <div className="cc-tile-sub"><Trend value={trend} /> <span className="text-[#94A3B8]">vs yesterday</span></div>}
        {spark && (
          <div className="cc-sparkline">
            <ResponsiveContainer width="100%" height={28}>
              <AreaChart data={spark}>
                <defs>
                  <linearGradient id={`grad-${label}`} x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor={accent} stopOpacity={0.4} />
                    <stop offset="100%" stopColor={accent} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Area type="monotone" dataKey="v" stroke={accent} strokeWidth={1.5} fill={`url(#grad-${label})`} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </button>
  );
}

const ONBOARDED_RANGES = {
  last_24h: { label: "24h", caption: "Onboarded in the last 24 hours" },
  last_week: { label: "Week", caption: "Onboarded in the last 7 days" },
  last_month: { label: "Month", caption: "Onboarded in the last 30 days" },
  last_year: { label: "Year", caption: "Onboarded in the last 12 months" },
};

function OnboardedTile({ data, testId = "cc-kpi-onboarded" }) {
  const [range, setRange] = useState("last_24h");
  const value = data ? data[range] : null;
  return (
    <div className="cc-tile" data-testid={testId}>
      <span className="cc-tile-icon" style={{ background: "#0B7E8C15", color: "#0B7E8C" }}>
        <UserCheck className="w-4 h-4" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-1">
          <div className="cc-tile-label">Patients Onboarded</div>
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger className="h-5 w-[62px] text-[10px] px-1.5 py-0 border-[#E5E7EB] shrink-0" data-testid="cc-kpi-onboarded-range">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(ONBOARDED_RANGES).map(([k, v]) => (
                <SelectItem key={k} value={k} className="text-xs" data-testid={`cc-kpi-onboarded-range-${k}`}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="cc-tile-value" data-testid="cc-kpi-onboarded-value">{value ?? "—"}</div>
        <div className="text-[10px] text-[#94A3B8] mt-0.5 leading-tight" data-testid="cc-kpi-onboarded-caption">{ONBOARDED_RANGES[range].caption}</div>
      </div>
    </div>
  );
}

const READMIT_RANGES = {
  last_24h: { label: "24h", caption: "Readmissions in the last 24 hours" },
  last_week: { label: "Week", caption: "Readmissions in the last 7 days" },
  last_month: { label: "Month", caption: "Readmissions in the last 30 days" },
  last_year: { label: "Year", caption: "Readmissions in the last 12 months" },
};

function ReadmissionsTile({ data, testId = "cc-kpi-readmissions" }) {
  const [range, setRange] = useState("last_month");
  const value = data ? data[range] : null;
  const rate = data?.rate_30d;
  return (
    <div className="cc-tile" data-testid={testId}>
      <span className="cc-tile-icon" style={{ background: "#D9770615", color: "#B45309" }}>
        <RotateCcw className="w-4 h-4" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-1">
          <div className="cc-tile-label">Readmissions</div>
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger className="h-5 w-[62px] text-[10px] px-1.5 py-0 border-[#E5E7EB] shrink-0" data-testid="cc-kpi-readmissions-range">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(READMIT_RANGES).map(([k, v]) => (
                <SelectItem key={k} value={k} className="text-xs" data-testid={`cc-kpi-readmissions-range-${k}`}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="cc-tile-value" data-testid="cc-kpi-readmissions-value">{value ?? "—"}</div>
        <div className="text-[10px] text-[#94A3B8] mt-0.5 leading-tight" data-testid="cc-kpi-readmissions-caption">
          {READMIT_RANGES[range].caption}
          {typeof rate === "number" && <span className="text-[#B45309] font-medium"> · {rate}% 48h rate (30d)</span>}
        </div>
      </div>
    </div>
  );
}



function RiskTile({ icon: Icon, label, value, pctVal, accent, bg }) {
  return (
    <div className="cc-risk-tile">
      <span className="cc-risk-tile-icon" style={{ background: bg, color: accent }}><Icon className="w-5 h-5" /></span>
      <div className="flex-1">
        <div className="cc-risk-tile-label">{label}</div>
        <div className="cc-risk-tile-value-row">
          <span className="cc-risk-tile-value">{value}</span>
          <span className="cc-risk-tile-pct" style={{ color: accent }}>{pctVal}%</span>
        </div>
      </div>
    </div>
  );
}

// =====================================================
// EXECUTIVE DASHBOARD
// =====================================================
export function ExecutiveDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [feed, setFeed] = useState(null);
  const [breaches, setBreaches] = useState([]);

  const load = useCallback(() => {
    Promise.all([
      api.get("/command/executive"),
      api.get("/command/executive/feed"),
      api.get("/command/thresholds/breaches"),
    ]).then(([e, f, b]) => { setData(e.data); setFeed(f.data); setBreaches(b.data.items || []); })
      .catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data || !feed) return <div className="p-6 text-sm text-[#6B7280]">Loading…</div>;
  const p = data.patient, c = data.clinical, o = data.operational, w = data.workforce;
  const cohort = c.stable + c.moderate + c.high + c.critical || 1;

  const riskData = [
    { name: "Stable", value: c.stable, color: "#10B981" },
    { name: "Moderate", value: c.moderate, color: "#F59E0B" },
    { name: "High", value: c.high, color: "#F97316" },
    { name: "Critical", value: c.critical, color: "#EF4444" },
  ];

  return (
    <div data-testid="cc-executive" className="cc-shell">
      <PageHeader title="Hospital @ Home Command Center" subtitle="Real-time operational and clinical intelligence"
        actions={<div className="flex items-center gap-2">
          <Button onClick={load} variant="outline" className="border-[#E5E7EB]"><RefreshCw className="w-4 h-4 mr-1.5" /> Refresh</Button>
        </div>}
      />

      {(breaches.length > 0 || (c.high + c.critical) > 0) && (
        <div className="cc-alert-banner" data-testid="cc-breach-banner">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-[#EF4444]" />
            <strong className="text-[#991B1B]">
              {(c.high + c.critical) || breaches.length} High Risk Patient{((c.high + c.critical) || breaches.length) === 1 ? "" : "s"} Require Immediate Review
            </strong>
          </div>
          <Button size="sm" variant="outline" onClick={() => navigate("/command/patient-explorer?risk=high")} className="border-[#FCA5A5] text-[#991B1B] hover:bg-[#FEE2E2]">View Patients</Button>
        </div>
      )}

      {/* Risk distribution tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-1">
        <RiskTile icon={ShieldCheck} label="Stable" value={c.stable} pctVal={pct(c.stable, cohort)} accent="#10B981" bg="#D1FAE5" />
        <RiskTile icon={ShieldAlert} label="Moderate Risk" value={c.moderate} pctVal={pct(c.moderate, cohort)} accent="#F59E0B" bg="#FEF3C7" />
        <RiskTile icon={ShieldAlert} label="High Risk" value={c.high} pctVal={pct(c.high, cohort)} accent="#F97316" bg="#FFEDD5" />
        <RiskTile icon={ShieldAlert} label="Critical" value={c.critical} pctVal={pct(c.critical, cohort)} accent="#EF4444" bg="#FEE2E2" />
      </div>
      <div className="text-[11px] text-[#6B7280] mb-4 flex items-center gap-1.5">
        <Users className="w-3 h-3" />
        Cohort: <span className="font-semibold text-[#1F2937]">{cohort}</span> active H@H patients · scored by NEWS2 from live vitals
      </div>

      {/* Operations strip with sparkline tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 mb-4">
        <StatTile icon={Users} accent="#0B7E8C" label="Active Patients" value={p.total_active} caption="H@H patients currently enrolled" onClick={() => navigate("/command/patient-explorer")} testId="cc-kpi-active" />
        <StatTile icon={UserPlus} accent="#10B981" label="New Admissions" value={p.new_admissions} caption="Admitted in the last 24 hours" />
        <OnboardedTile data={p.onboarded} />
        <ReadmissionsTile data={p.readmissions} />
        <StatTile icon={BellRing} accent="#F59E0B" label="Active Alerts" value={c.active_alerts} caption="RPM alerts unacknowledged" />
        <StatTile icon={Video} accent="#0B7E8C" label="Teleconsults Today" value={o.teleconsults_today} caption="Scheduled or completed today" onClick={() => navigate("/teleconsult")} />
        <StatTile icon={MapPin} accent="#10B981" label="Home Visits Today" value={o.home_visits_today} caption="Fleet visits dispatched today" onClick={() => navigate("/fleet/visits")} />
        <StatTile icon={Phone} accent="#8B5CF6" label="Hotline Calls Today" value={o.hotline_calls_today} caption="Calls received today" onClick={() => navigate("/hotline")} />
      </div>

      {/* Middle band: risk donut + region map placeholder + interventions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
        {/* Risk donut */}
        <div className="cc-chart-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-[#1F2937]">Patient Risk Distribution</h3>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={riskData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={2}>
                {riskData.map((d, i) => <Cell key={i} fill={d.color} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="text-center -mt-32 pointer-events-none">
            <div className="text-3xl font-bold text-[#1F2937]">{cohort}</div>
            <div className="text-xs text-[#6B7280]">Total Patients</div>
          </div>
          <div className="mt-32 space-y-2">
            {riskData.map((d) => (
              <div key={d.name} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: d.color }} /> {d.name}</span>
                <span className="text-[#1F2937]">{d.value} <span className="text-[#6B7280]">({pct(d.value, cohort)}%)</span></span>
              </div>
            ))}
          </div>
          <button onClick={() => navigate("/command/patient-explorer")} className="mt-3 text-sm text-[#0B7E8C] font-semibold hover:underline flex items-center gap-1">View Full Risk Report <ArrowUpRight className="w-3 h-3" /></button>
        </div>

        {/* Distribution by facility */}
        <div className="cc-chart-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-[#1F2937]">Patients by Facility</h3>
          </div>
          <ResponsiveContainer width="100%" height={290}>
            <BarChart data={(feed.top_facilities || []).slice(0, 6)} layout="vertical" margin={{ left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={140} />
              <Tooltip />
              <Bar dataKey="count" fill="#0B7E8C" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Active Interventions */}
        <div className="cc-chart-card">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-[#1F2937]">Active Interventions</h3>
            <button onClick={() => navigate("/command/interventions")} className="text-xs text-[#0B7E8C] font-semibold hover:underline">View All</button>
          </div>
          <div>
            {(feed.interventions || []).map((i) => {
              const tone = i.severity === "critical" || i.severity === "high" ? { color: "#EF4444", bg: "#FEE2E2" } :
                            i.severity === "medium" || i.severity === "urgent" ? { color: "#F59E0B", bg: "#FEF3C7" } :
                            { color: "#10B981", bg: "#D1FAE5" };
              const Icon = i.type === "rpm_alert" ? AlertTriangle : i.type === "order" ? Pill : i.type === "teleconsult" ? Video : ClipboardList;
              return (
                <div key={i.id} className="cc-intervention" data-testid={`cc-exec-intervention-${i.id}`}>
                  <span className="cc-intervention-icon" style={{ background: tone.bg, color: tone.color }}><Icon className="w-4 h-4" /></span>
                  <div className="flex-1 min-w-0">
                    <div className="cc-intervention-title truncate">{i.title}</div>
                    <div className="cc-intervention-sub">{i.patient_mrn} · {i.patient_name}</div>
                  </div>
                  <div className="cc-intervention-meta">
                    <div style={{ color: tone.color, fontWeight: 700 }} className="capitalize">{i.severity}</div>
                    <div className="text-[10px]">{timeAgo(i.at)}</div>
                  </div>
                </div>
              );
            })}
            {(feed.interventions || []).length === 0 && <div className="text-sm text-[#6B7280] py-3">No active interventions.</div>}
          </div>
        </div>
      </div>

      {/* SLA Performance Overview */}
      <h3 className="cc-section-title">SLA Performance Overview</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <SlaMini title="Onboarding SLA" rate={`${pct(0, 1)}%`} primaryLabel="Conversion Rate" link="/command/sla"
          rows={[["Pending Medical Review", null], ["Pending Operational Review", null], ["Approved", null], ["Visit Completed", null]]} />
        <SlaMini title="RPM SLA" rate="—" primaryLabel="Resolution Rate" link="/command/sla"
          rows={[["Active Alerts", c.active_alerts], ["Escalated", c.escalated], ["Critical Patients", c.critical], ["Stable", c.stable]]} />
        <SlaMini title="Hotline SLA" rate="—" primaryLabel="Closure Rate" link="/command/sla"
          rows={[["Calls Today", o.hotline_calls_today], ["Active Orders", o.active_interventions], ["Delayed Visits", o.delayed_visits]]} />
        <SlaMini title="Teleconsult SLA" rate="—" primaryLabel="Completion Rate" link="/command/sla"
          rows={[["Today", o.teleconsults_today], ["Active Interventions", o.active_interventions]]} />
      </div>

      {/* Top Diagnoses + Live Activity Feed */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <div className="cc-chart-card">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-[#1F2937]">Top Diagnoses</h3>
            <button onClick={() => navigate("/reports")} className="text-xs text-[#0B7E8C] font-semibold hover:underline">View Report</button>
          </div>
          {(feed.top_diagnoses || []).map((d, i) => (
            <div key={i} className="flex items-center gap-3 mb-2.5">
              <span className="text-sm text-[#1F2937] w-40 truncate">{d.diagnosis}</span>
              <div className="flex-1 bg-[#F1F5F9] rounded-full h-3 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#0B7E8C] to-[#14B8A6] rounded-full" style={{ width: `${d.pct}%` }} />
              </div>
              <span className="text-sm font-semibold text-[#1F2937] w-12 text-right">{d.pct}%</span>
            </div>
          ))}
          {(feed.top_diagnoses || []).length === 0 && <div className="text-sm text-[#6B7280] py-3">No diagnoses recorded.</div>}
        </div>

        <div className="cc-chart-card">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-[#1F2937]">Live Activity Feed</h3>
            <button onClick={() => navigate("/command/interventions")} className="text-xs text-[#0B7E8C] font-semibold hover:underline">View All</button>
          </div>
          {(feed.feed || []).map((f, i) => {
            const tone = f.severity === "critical" || f.severity === "high" ? { color: "#EF4444", bg: "#FEE2E2" } :
                          f.severity === "medium" || f.severity === "urgent" ? { color: "#F59E0B", bg: "#FEF3C7" } :
                          { color: "#0B7E8C", bg: "#EAF8FA" };
            const Icon = f.type === "rpm_alert" ? AlertTriangle : f.type === "teleconsult_completed" ? Video :
                          f.type === "home_visit_completed" ? HomeIcon : Activity;
            return (
              <div key={i} className="cc-feed-item">
                <div className="cc-feed-time">{new Date(f.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                <span className="cc-feed-icon" style={{ background: tone.bg, color: tone.color }}><Icon className="w-3.5 h-3.5" /></span>
                <div className="flex-1 min-w-0">
                  <div className="cc-feed-title">{f.title}</div>
                  <div className="cc-feed-sub">{f.subtitle}</div>
                </div>
              </div>
            );
          })}
          {(feed.feed || []).length === 0 && <div className="text-sm text-[#6B7280] py-3">No recent activity.</div>}
        </div>
      </div>

      {/* Active Workforce (real on-shift staff counts) */}
      <h3 className="cc-section-title">Active Workforce</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <UtilCard icon={Users} accent="#0B7E8C" bg="#EAF8FA" label="Physicians" count={w.active_physicians} />
        <UtilCard icon={Users} accent="#10B981" bg="#D1FAE5" label="Nurses" count={w.active_nurses} />
        <UtilCard icon={Users} accent="#8B5CF6" bg="#EDE9FE" label="Coordinators" count={w.active_coordinators} />
        <UtilCard icon={Users} accent="#F59E0B" bg="#FEF3C7" label="Home Care Teams" count={w.active_home_teams} />
      </div>
    </div>
  );
}

function SlaMini({ title, rate, primaryLabel, rows, link }) {
  const navigate = useNavigate();
  return (
    <div className="cc-chart-card">
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-semibold text-[#1F2937] text-sm">{title}</h4>
        <span className="text-[#10B981] font-bold text-sm">{rate}</span>
      </div>
      <div className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-2">{primaryLabel}</div>
      <div className="cc-sla-card">
        {rows.map(([k, v], i) => (
          <div key={i} className="cc-row"><span>{k}</span><span className="num">{v ?? "—"}</span></div>
        ))}
      </div>
      <button onClick={() => navigate(link)} className="mt-2 text-xs text-[#0B7E8C] font-semibold hover:underline flex items-center gap-1">View Details <ArrowUpRight className="w-3 h-3" /></button>
    </div>
  );
}

function UtilCard({ icon: Icon, accent, bg, label, count }) {
  return (
    <div className="cc-card flex items-center gap-4">
      <span className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: bg, color: accent }}><Icon className="w-5 h-5" /></span>
      <div className="flex-1">
        <div className="flex items-baseline justify-between">
          <span className="text-sm text-[#6B7280] font-medium">{label}</span>
          <span className="text-2xl font-bold text-[#111827]">{count}</span>
        </div>
        <div className="text-[10px] text-[#6B7280] mt-1 text-right">currently active</div>
      </div>
    </div>
  );
}

// =====================================================
// OPERATIONAL DASHBOARD (reference layout)
// =====================================================
export function OperationalDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [exec, setExec] = useState(null);
  useEffect(() => {
    Promise.all([api.get("/command/operational"), api.get("/command/executive")])
      .then(([o, e]) => { setData(o.data); setExec(e.data); })
      .catch((e) => toast.error(formatApiError(e)));
  }, []);
  if (!data || !exec) return <div className="p-6 text-sm text-[#6B7280]">Loading…</div>;
  const onb = data.onboarding, fl = data.fleet, tc = data.teleconsultation, ht = data.hotline, t = data.tasks;
  const p = exec.patient;
  const totalOnb = onb.pending_medical_review + onb.pending_operational_review + onb.approved + onb.visit_completed + onb.rejected || 1;
  const approvalRate = pct(onb.approved + onb.visit_completed, totalOnb);
  const rejectionRate = pct(onb.rejected, totalOnb);
  const totalVisits = Object.values(fl).reduce((a, b) => a + b, 0) || 1;
  const completionRateFleet = pct(fl.completed, totalVisits);
  const totalTele = Object.values(tc).reduce((a, b) => a + b, 0) || 1;
  const teleCompletionRate = pct(tc.completed, totalTele);
  const totalTasksOverview = t.open + t.overdue || 1;

  const pipelineStages = [
    { name: "Medical Review", value: onb.pending_medical_review, color: "#0B7E8C" },
    { name: "Ops Review", value: onb.pending_operational_review, color: "#10B981" },
    { name: "Approved", value: onb.approved, color: "#F59E0B" },
    { name: "Completed", value: onb.visit_completed, color: "#3B82F6" },
    { name: "Rejected", value: onb.rejected, color: "#EF4444" },
  ];

  return (
    <div data-testid="cc-operational" className="cc-shell">
      <PageHeader title="Operational Dashboard" subtitle="Workflow performance across modules" />

      {/* Top KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        <StatTile icon={Users} accent="#0B7E8C" label="Active Patients" value={p.total_active} caption="H@H patients currently enrolled" />
        <StatTile icon={UserPlus} accent="#10B981" label="New Admissions" value={p.new_admissions} caption="Admitted in the last 24 hours" />
        <StatTile icon={UserPlus} accent="#8B5CF6" label="New Activations" value={p.new_activations} caption="Onboarded today" />
        <StatTile icon={UserMinus} accent="#F59E0B" label="Discharges" value={p.discharges} caption="Discharged today" />
        <StatTile icon={UserMinus} accent="#EF4444" label="Rejected Referrals" value={p.rejected_referrals} caption="Declined during onboarding" />
        <StatTile icon={Activity} accent="#8B5CF6" label="Avg Onboarding Time" value={p.avg_onboarding_days ? `${p.avg_onboarding_days}d` : "—"} caption="Submit → activation, last 30d" />
      </div>

      {/* Onboarding pipeline + performance */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
        <div className="cc-chart-card lg:col-span-2">
          <h3 className="font-semibold text-[#1F2937] mb-3">Onboarding Pipeline <span className="text-xs font-normal text-[#6B7280]">· Total: {totalOnb}</span></h3>
          <div className="grid grid-cols-5 gap-2">
            {pipelineStages.map((s) => (
              <div key={s.name} className="text-center p-3 rounded-lg" style={{ background: `${s.color}10`, border: `1px solid ${s.color}30` }}>
                <div className="text-xs font-semibold mb-1" style={{ color: s.color }}>{s.name}</div>
                <div className="text-2xl font-bold text-[#1F2937]">{s.value}</div>
                <div className="text-[10px] text-[#6B7280] mt-1">{pct(s.value, totalOnb)}%</div>
              </div>
            ))}
          </div>
        </div>
        <div className="cc-chart-card">
          <h3 className="font-semibold text-[#1F2937] mb-3">Onboarding Performance</h3>
          {[
            { label: "Approval Rate", value: approvalRate, color: "#0B7E8C" },
            { label: "Rejection Rate", value: rejectionRate, color: "#EF4444" },
            { label: "Completion Rate", value: pct(onb.visit_completed, totalOnb), color: "#F59E0B" },
          ].map((r) => (
            <div key={r.label} className="mb-3">
              <div className="flex items-center justify-between mb-1.5"><span className="text-xs text-[#6B7280]">{r.label}</span><strong className="text-sm text-[#1F2937]">{r.value}%</strong></div>
              <div className="cc-util-bar"><div className="cc-util-fill" style={{ width: `${r.value}%`, background: r.color }} /></div>
            </div>
          ))}
          <Button size="sm" onClick={() => navigate("/command/sla")} className="w-full mt-3 bg-transparent border border-[#0B7E8C] text-[#0B7E8C] hover:bg-[#EAF8FA]"><ArrowUpRight className="w-3 h-3 mr-1" />View Onboarding SLA</Button>
        </div>
      </div>

      {/* Fleet + Teleconsult cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4">
        <div className="cc-chart-card">
          <h3 className="font-semibold text-[#1F2937] mb-3">Fleet / Home Visits <span className="text-xs font-normal text-[#6B7280]">· Total: {totalVisits}</span></h3>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5 mb-3">
            {Object.entries(fl).map(([k, v]) => (
              <div key={k} className="text-center p-2 rounded-lg bg-[#F8FAFC] border border-[#E5E7EB]">
                <div className="text-base font-bold text-[#1F2937]">{v}</div>
                <div className="text-[9px] uppercase tracking-wider text-[#6B7280] capitalize">{k.replace(/_/g, " ")}</div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-2 mt-2">
            <DonutMini value={completionRateFleet} label="Completion Rate" />
            <DonutMini value={fl?.on_time_pct || 0} label="On-Time Performance" />
            <div className="text-center"><div className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">Avg Visit Duration</div><div className="text-2xl font-bold text-[#1F2937]">{fl?.avg_visit_min ? `${fl.avg_visit_min} min` : "—"}</div></div>
          </div>
        </div>
        <div className="cc-chart-card">
          <h3 className="font-semibold text-[#1F2937] mb-3">Teleconsultations <span className="text-xs font-normal text-[#6B7280]">· Total: {totalTele}</span></h3>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5 mb-3">
            {Object.entries(tc).map(([k, v]) => (
              <div key={k} className="text-center p-2 rounded-lg bg-[#F8FAFC] border border-[#E5E7EB]">
                <div className="text-base font-bold text-[#1F2937]">{v}</div>
                <div className="text-[9px] uppercase tracking-wider text-[#6B7280] capitalize">{k.replace(/_/g, " ")}</div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-2 mt-2">
            <DonutMini value={teleCompletionRate} label="Completion Rate" />
            <DonutMini value={pct(tc.no_show || 0, totalTele)} label="No Show Rate" tone="warn" />
            <div className="text-center"><div className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">Avg Consult Duration</div><div className="text-2xl font-bold text-[#1F2937]">{tc?.avg_duration_min ? `${tc.avg_duration_min} min` : "—"}</div></div>
          </div>
        </div>
      </div>

      {/* Hotline / Tasks / Tasks Overview / Operational Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
        <div className="cc-chart-card">
          <h3 className="font-semibold text-[#1F2937] mb-3">Hotline / Tasks</h3>
          <div className="grid grid-cols-4 gap-1.5">
            {[["Calls Today", ht.today, "#F59E0B"], ["Open Sessions", ht.open, "#3B82F6"], ["Escalated", ht.escalated, "#EF4444"], ["Follow-Ups", ht.follow_up, "#8B5CF6"]].map(([l, v, c]) => (
              <div key={l} className="text-center p-2 rounded-lg" style={{ background: `${c}10`, border: `1px solid ${c}30` }}>
                <div className="text-lg font-bold text-[#1F2937]">{v}</div><div className="text-[9px] uppercase tracking-wider" style={{ color: c }}>{l}</div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-2 mt-3">
            <DonutMini value={ht.closure_rate_pct || 0} label="Closure Rate" />
            <div className="text-center"><div className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">Open Tasks</div><div className="text-2xl font-bold text-[#10B981]">{t.open}</div></div>
            <div className="text-center"><div className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">Overdue MAR</div><div className="text-2xl font-bold text-[#EF4444]">{t.overdue}</div></div>
          </div>
        </div>
        <div className="cc-chart-card">
          <h3 className="font-semibold text-[#1F2937] mb-3">Tasks Overview</h3>
          <div className="relative">
            <ResponsiveContainer width="100%" height={170}>
              <PieChart>
                <Pie data={[{ name: "Open", value: t.open, color: "#10B981" }, { name: "Overdue", value: t.overdue, color: "#EF4444" }]} dataKey="value" innerRadius={45} outerRadius={70}>
                  {[{ c: "#10B981" }, { c: "#EF4444" }].map((d, i) => <Cell key={i} fill={d.c} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <div className="text-2xl font-bold text-[#1F2937]">{totalTasksOverview}</div>
              <div className="text-[10px] text-[#6B7280]">Total Tasks</div>
            </div>
          </div>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between"><span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#10B981]" />Open</span><span>{t.open} ({pct(t.open, totalTasksOverview)}%)</span></div>
            <div className="flex justify-between"><span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#EF4444]" />Overdue</span><span>{t.overdue} ({pct(t.overdue, totalTasksOverview)}%)</span></div>
          </div>
        </div>
        <div className="cc-chart-card">
          <h3 className="font-semibold text-[#1F2937] mb-3">Operational Alerts</h3>
          {[
            { l: "Missed Home Visits", v: 0, c: "#EF4444" },
            { l: "Delayed Visits", v: fl.delayed || 0, c: "#F97316" },
            { l: "Unassigned Visits", v: fl.scheduled || 0, c: "#F59E0B" },
            { l: "Overdue Tasks", v: t.overdue, c: "#EF4444" },
            { l: "Pending Discharges", v: 0, c: "#94A3B8" },
          ].map((a) => (
            <div key={a.l} className="flex items-center justify-between py-2 border-b border-[#F1F5F9] last:border-b-0">
              <span className="text-sm text-[#1F2937] flex items-center gap-2"><AlertTriangle className="w-3.5 h-3.5" style={{ color: a.c }} />{a.l}</span>
              <strong className="text-sm" style={{ color: a.c }}>{a.v}</strong>
            </div>
          ))}
          <Button size="sm" onClick={() => navigate("/command/interventions")} className="w-full mt-2 bg-transparent border border-[#0B7E8C] text-[#0B7E8C] hover:bg-[#EAF8FA]">View All Alerts <ArrowUpRight className="w-3 h-3 ml-1" /></Button>
        </div>
      </div>

      {/* SLA Performance footer */}
      <h3 className="cc-section-title">SLA Performance</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[["Onboarding SLA", "#0B7E8C", Users], ["Fleet SLA", "#10B981", MapPin], ["Teleconsult SLA", "#3B82F6", Video], ["Hotline SLA", "#F59E0B", Phone]].map(([title, color, Icon]) => (
          <button key={title} onClick={() => navigate("/command/sla")} className="cc-card flex flex-col items-center text-center hover:shadow-md transition">
            <span className="w-12 h-12 rounded-full flex items-center justify-center mb-2" style={{ background: `${color}15`, color }}><Icon className="w-6 h-6" /></span>
            <div className="font-semibold text-[#1F2937] mb-1">{title}</div>
            <div className="text-xs text-[#0B7E8C] font-semibold">View Details →</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function DonutMini({ value, label, tone = "good", trend }) {
  const color = tone === "warn" ? "#F59E0B" : tone === "bad" ? "#EF4444" : "#0B7E8C";
  return <RingGauge value={value} label={label} trend={trend} color={color} size={72} stroke={8} />;
}

// =====================================================
// CLINICAL SAFETY (reference layout)
// =====================================================
export function ClinicalSafetyDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [exec, setExec] = useState(null);
  const [feed, setFeed] = useState(null);
  const [explorer, setExplorer] = useState([]);
  const [interventions, setInterventions] = useState({ items: [], kpis: { by_source: {} } });

  useEffect(() => {
    Promise.all([
      api.get("/command/clinical"),
      api.get("/command/executive"),
      api.get("/command/executive/feed"),
      api.get("/command/patient-explorer", { params: { risk: "high" } }),
      api.get("/command/interventions"),
    ]).then(([c, e, f, p, i]) => { setData(c.data); setExec(e.data); setFeed(f.data); setExplorer(p.data.items || []); setInterventions(i.data); })
      .catch((e) => toast.error(formatApiError(e)));
  }, []);
  if (!data || !exec || !feed) return <div className="p-6 text-sm text-[#6B7280]">Loading…</div>;
  const r = data.rpm_risk, a = data.alerts, m = data.medications, d = data.documentation;
  const c = exec.clinical;
  const cohort = r.stable + r.moderate + r.high + r.critical || 1;
  const totalMar = (m.active || 0) + (m.missed || 0) + (m.held || 0) + (m.refused || 0) + (m.late || 0) || 1;
  const compliance = pct(m.active, totalMar);
  const riskDist = [
    { name: "Stable", value: r.stable, color: "#10B981" },
    { name: "Moderate", value: r.moderate, color: "#F59E0B" },
    { name: "High", value: r.high, color: "#F97316" },
    { name: "Critical", value: r.critical, color: "#EF4444" },
  ];

  return (
    <div data-testid="cc-clinical" className="cc-shell">
      <PageHeader title="Clinical Command Center" subtitle="Real-time clinical overview and patient safety monitoring" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
        <div className="cc-chart-card">
          <h3 className="font-semibold text-[#1F2937] mb-3">Patient Risk Distribution</h3>
          <div className="relative">
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={riskDist} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={75} paddingAngle={2}>
                  {riskDist.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <div className="text-3xl font-bold text-[#1F2937]">{cohort}</div>
              <div className="text-xs text-[#6B7280]">Active Patients</div>
            </div>
          </div>
          <div className="space-y-1 text-sm mt-2">
            {riskDist.map((d) => (
              <div key={d.name} className="flex justify-between"><span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: d.color }} />{d.name}</span><span>{d.value} ({pct(d.value, cohort)}%)</span></div>
            ))}
          </div>
        </div>
        <div className="cc-chart-card">
          <div className="flex justify-between items-center mb-3"><h3 className="font-semibold text-[#1F2937]">High Risk Patients Queue</h3><button onClick={() => navigate("/command/patient-explorer?risk=high")} className="text-xs text-[#0B7E8C] font-semibold hover:underline">View All</button></div>
          <div className="space-y-1.5">
            {explorer.slice(0, 5).map((p) => (
              <div key={p.id} className="flex items-center justify-between p-2 rounded hover:bg-[#FAFBFC] cursor-pointer" onClick={() => navigate(`/clinical/patients/${p.id}`)}>
                <div className="text-xs"><div className="font-medium text-[#1F2937]"><CopyableId value={p.mrn} kind="MRN" /></div><div className="text-[10px] text-[#6B7280]">{p.name}</div></div>
                <div className="text-xs font-bold text-[#1F2937]">{p.news2_score ?? "—"}</div>
                <span className="px-1.5 py-0.5 rounded text-[10px] uppercase font-bold" style={{ background: `${RISK_COLORS[p.risk_level]}15`, color: RISK_COLORS[p.risk_level] }}>{p.risk_level}</span>
              </div>
            ))}
            {explorer.length === 0 && <div className="text-xs text-[#6B7280] py-4 text-center">No high-risk patients.</div>}
          </div>
        </div>
        <div className="cc-chart-card">
          <div className="flex justify-between items-center mb-3"><h3 className="font-semibold text-[#1F2937]">Latest Clinical Activity</h3><button onClick={() => navigate("/command/interventions")} className="text-xs text-[#0B7E8C] font-semibold hover:underline">View All</button></div>
          {(feed.feed || []).slice(0, 6).map((f, i) => {
            const tone = f.severity === "critical" || f.severity === "high" ? { color: "#EF4444", bg: "#FEE2E2" } : f.severity === "medium" ? { color: "#F59E0B", bg: "#FEF3C7" } : { color: "#0B7E8C", bg: "#EAF8FA" };
            const Icon = f.type === "rpm_alert" ? AlertTriangle : f.type === "teleconsult_completed" ? Video : f.type === "home_visit_completed" ? HomeIcon : Activity;
            return (
              <div key={i} className="cc-feed-item">
                <div className="cc-feed-time">{new Date(f.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                <span className="cc-feed-icon" style={{ background: tone.bg, color: tone.color }}><Icon className="w-3.5 h-3.5" /></span>
                <div className="flex-1 min-w-0"><div className="cc-feed-title text-xs">{f.title}</div><div className="cc-feed-sub">{f.subtitle}</div></div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
        <div className="cc-chart-card">
          <div className="flex justify-between items-center mb-3"><h3 className="font-semibold text-[#1F2937]">Active Interventions</h3><button onClick={() => navigate("/command/interventions")} className="text-xs text-[#0B7E8C] font-semibold hover:underline">View All</button></div>
          <div className="grid grid-cols-4 gap-1.5 mb-3">
            {[["Open", interventions.kpis?.open, "#8B5CF6"], ["Escalated", interventions.kpis?.escalated, "#EF4444"], ["Overdue", interventions.kpis?.overdue, "#F97316"], ["Resolved Today", interventions.kpis?.completed_today, "#10B981"]].map(([l, v, c]) => (
              <div key={l} className="text-center p-2 rounded-lg" style={{ background: `${c}10`, border: `1px solid ${c}30` }}>
                <div className="text-lg font-bold text-[#1F2937]">{v ?? 0}</div><div className="text-[9px] uppercase tracking-wider" style={{ color: c }}>{l}</div>
              </div>
            ))}
          </div>
          <div className="space-y-1 text-xs">
            {(interventions.items || []).slice(0, 5).map((i) => (
              <div key={i.id} className="flex items-center justify-between p-1.5 border-b border-[#F1F5F9]">
                <span className="truncate flex-1 text-[#1F2937]">{i.title}</span>
                <span className="text-[10px] text-[#6B7280] ml-2">{i.patient_mrn}</span>
                <span className="ml-2 px-1.5 py-0.5 rounded text-[9px] uppercase font-bold" style={{ background: `${SOURCE_COLORS[i.source]}15`, color: SOURCE_COLORS[i.source] }}>{i.priority}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="cc-chart-card">
          <div className="flex justify-between items-center mb-2"><h3 className="font-semibold text-[#1F2937]">Medication Compliance (MAR)</h3><button onClick={() => navigate("/clinical/tasks")} className="text-xs text-[#0B7E8C] font-semibold hover:underline">View All</button></div>
          <div className="flex items-center gap-4">
            <div className="relative">
              <PieChart width={120} height={120}>
                <Pie data={[{ v: compliance }, { v: 100 - compliance }]} dataKey="v" cx={60} cy={60} innerRadius={40} outerRadius={55} startAngle={90} endAngle={-270} stroke="none">
                  <Cell fill="#10B981" /><Cell fill="#F1F5F9" />
                </Pie>
              </PieChart>
              <div className="absolute inset-0 flex flex-col items-center justify-center"><div className="text-xl font-bold text-[#1F2937]">{compliance}%</div><div className="text-[10px] text-[#6B7280]">Compliance</div></div>
            </div>
            <div className="flex-1 grid grid-cols-2 gap-1.5 text-xs">
              <div className="text-center p-1.5 rounded bg-[#EAF8FA]"><div className="font-bold text-[#1F2937]">{m.active}</div><div className="text-[9px] text-[#6B7280]">Active</div></div>
              <div className="text-center p-1.5 rounded bg-[#FEE2E2]"><div className="font-bold text-[#1F2937]">{m.missed}</div><div className="text-[9px] text-[#6B7280]">Missed</div></div>
              <div className="text-center p-1.5 rounded bg-[#FEF3C7]"><div className="font-bold text-[#1F2937]">{m.held}</div><div className="text-[9px] text-[#6B7280]">Held</div></div>
              <div className="text-center p-1.5 rounded bg-[#FED7AA]"><div className="font-bold text-[#1F2937]">{m.late}</div><div className="text-[9px] text-[#6B7280]">Late</div></div>
            </div>
          </div>
        </div>
        <div className="cc-chart-card">
          <div className="flex justify-between items-center mb-3"><h3 className="font-semibold text-[#1F2937]">Clinical Documentation Activity</h3></div>
          <div className="grid grid-cols-4 gap-1.5 mb-3">
            {[["Physician", d.physician_notes, "#0B7E8C"], ["Nursing", d.nursing_notes, "#10B981"], ["Coord.", d.coordination_notes, "#F59E0B"], ["Encounters", d.encounters, "#3B82F6"]].map(([l, v, c]) => (
              <div key={l} className="text-center p-2 rounded-lg" style={{ background: `${c}10`, border: `1px solid ${c}30` }}>
                <div className="text-base font-bold text-[#1F2937]">{v}</div><div className="text-[9px] uppercase tracking-wider" style={{ color: c }}>{l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Patient Cohorts + Clinical Bottlenecks */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="cc-chart-card">
          <h3 className="font-semibold text-[#1F2937] mb-3">Patient Cohorts</h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><div className="text-xs uppercase tracking-wider text-[#6B7280] mb-1.5">By Diagnosis (Top 5)</div>
              {(feed.top_diagnoses || []).slice(0, 5).map((d, i) => (
                <div key={i} className="flex justify-between text-xs py-0.5"><span className="text-[#1F2937] truncate">{d.diagnosis}</span><span className="font-semibold">{d.pct}%</span></div>
              ))}</div>
            <div><div className="text-xs uppercase tracking-wider text-[#6B7280] mb-1.5">By RPM Status</div>
              <div className="flex justify-between text-xs py-0.5"><span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#10B981]" />Monitoring</span><span>{r.stable}</span></div>
              <div className="flex justify-between text-xs py-0.5"><span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#F59E0B]" />Alert</span><span>{c.active_alerts}</span></div>
              <div className="flex justify-between text-xs py-0.5"><span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#EF4444]" />Intervention</span><span>{r.high + r.critical}</span></div>
            </div>
          </div>
        </div>
        <div className="cc-chart-card">
          <h3 className="font-semibold text-[#1F2937] mb-3">Clinical Bottlenecks</h3>
          <div className="grid grid-cols-5 gap-2">
            {[["Unacknowledged Alerts", a.new, "#EF4444", BellRing],
              ["Overdue Interventions", interventions.kpis?.overdue || 0, "#F97316", AlertTriangle],
              ["Missed MAR", m.missed, "#EF4444", Pill],
              ["Pending Teleconsults", c.escalated || 0, "#3B82F6", Video],
              ["Pending Home Visits", 0, "#10B981", HomeIcon],
            ].map(([l, v, c, Icon]) => (
              <div key={l} className="text-center p-2 rounded-lg border border-[#E5E7EB]">
                <span className="w-8 h-8 rounded-full mx-auto flex items-center justify-center mb-1" style={{ background: `${c}15`, color: c }}><Icon className="w-4 h-4" /></span>
                <div className="text-[9px] uppercase tracking-wider text-[#6B7280] mb-0.5">{l}</div>
                <div className="text-xl font-bold text-[#1F2937]">{v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// =====================================================
// POPULATION HEALTH (reference layout)
// =====================================================
export function PopulationHealthDashboard() {
  const GENDER_COLORS = ["#0B7E8C", "#EC4899", "#F59E0B", "#8B5CF6"];
  const DISEASE_COLORS = ["#0B7E8C", "#8B5CF6", "#F59E0B", "#EF4444", "#10B981"];
  const TEAM_COLORS = ["#0B7E8C", "#8B5CF6", "#F59E0B", "#EF4444", "#10B981", "#EC4899", "#14B8A6", "#6366F1"];
  const [data, setData] = useState(null);
  const [explorer, setExplorer] = useState([]);
  const [facilityId, setFacilityId] = useState("all");
  const [risk, setRisk] = useState("all");

  const load = useCallback(() => {
    const params = {};
    if (facilityId !== "all") params.facility_id = facilityId;
    if (risk !== "all") params.risk = risk;
    Promise.all([
      api.get("/command/population", { params }),
      api.get("/command/patient-explorer", { params: { risk: "high" } }),
    ]).then(([p, e]) => { setData(p.data); setExplorer(e.data.items || []); })
      .catch((e) => toast.error(formatApiError(e)));
  }, [facilityId, risk]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="p-6 text-sm text-[#6B7280]">Loading…</div>;

  const female = data.by_gender.find((g) => g.bucket === "Female")?.count || 0;
  const chronicConditions = data.by_diagnosis.reduce((s, d) => s + d.count, 0);
  const highRisk = (data.by_risk.find((r) => r.bucket === "high")?.count || 0) + (data.by_risk.find((r) => r.bucket === "critical")?.count || 0);

  return (
    <div data-testid="cc-population" className="cc-shell">
      <PageHeader title="Population Health Dashboard" subtitle="Cohort analysis and population insights" />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
        <StatTile icon={Users} accent="#0B7E8C" label="Total Active Patients" value={data.total} />
        <StatTile icon={ShieldAlert} accent="#F59E0B" label="High Risk Patients" value={highRisk} />
        <StatTile icon={Activity} accent="#8B5CF6" label="Avg Age (Years)" value={data.avg_age_years || 0} />
        <StatTile icon={Users} accent="#EC4899" label="Female Patients" value={`${female} (${pct(female, data.total)}%)`} />
        <StatTile icon={HeartPulse} accent="#EF4444" label="Chronic Conditions" value={data.chronic_conditions ?? chronicConditions} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
        <div className="cc-chart-card">
          <h4 className="font-semibold text-[#1F2937] text-sm mb-2">Patients by Risk Level</h4>
          <div className="relative">
            <ResponsiveContainer width="100%" height={200}>
              <PieChart><Pie data={data.by_risk} dataKey="count" nameKey="bucket" cx="50%" cy="50%" innerRadius={50} outerRadius={75}>
                {data.by_risk.map((d, i) => <Cell key={i} fill={RISK_COLORS[d.bucket] || "#94A3B8"} />)}
              </Pie><Tooltip /></PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none"><div className="text-3xl font-bold text-[#1F2937]">{data.total}</div><div className="text-xs text-[#6B7280]">Total</div></div>
          </div>
          <div className="space-y-1 text-xs mt-2">
            {data.by_risk.map((d) => (
              <div key={d.bucket} className="flex justify-between"><span className="flex items-center gap-1.5 capitalize"><span className="w-2 h-2 rounded-full" style={{ background: RISK_COLORS[d.bucket] || "#94A3B8" }} />{d.bucket}</span><span>{d.count} ({pct(d.count, data.total)}%)</span></div>
            ))}
          </div>
        </div>
        <div className="cc-chart-card">
          <h4 className="font-semibold text-[#1F2937] text-sm mb-2">Patients by Diagnosis (Top 5)</h4>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.by_diagnosis.slice(0, 5)} layout="vertical"><CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" /><XAxis type="number" tick={{ fontSize: 10 }} /><YAxis dataKey="bucket" type="category" tick={{ fontSize: 10 }} width={110} /><Tooltip /><Bar dataKey="count" fill="#0B7E8C" radius={[0, 4, 4, 0]} /></BarChart>
          </ResponsiveContainer>
        </div>
        <div className="cc-chart-card">
          <h4 className="font-semibold text-[#1F2937] text-sm mb-2">Patients by Age Group</h4>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.by_age}><CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" /><XAxis dataKey="bucket" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} /><Tooltip /><Bar dataKey="count" fill="#14B8A6" radius={[4, 4, 0, 0]} /></BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
        <div className="cc-chart-card">
          <h4 className="font-semibold text-[#1F2937] text-sm mb-2">Patients by Facility</h4>
          <div className="overflow-x-auto"><table className="w-full text-xs">
            <thead><tr className="text-[#6B7280] text-[10px] uppercase"><th className="text-left py-1">Facility</th><th className="text-right">#</th><th className="text-right">%</th></tr></thead>
            <tbody>{data.by_facility.slice(0, 5).map((f) => (
              <tr key={f.facility_id} className="border-t border-[#F1F5F9]">
                <td className="py-1.5 text-[#1F2937]">{f.facility}</td>
                <td className="text-right">{f.patients}</td>
                <td className="text-right">{pct(f.patients, data.total)}%</td>
              </tr>
            ))}</tbody>
          </table></div>
        </div>
        <div className="cc-chart-card">
          <h4 className="font-semibold text-[#1F2937] text-sm mb-2">Patients by Gender</h4>
          <div className="relative">
            <ResponsiveContainer width="100%" height={180}>
              <PieChart><Pie data={data.by_gender} dataKey="count" nameKey="bucket" cx="50%" cy="50%" innerRadius={40} outerRadius={65}>
                {data.by_gender.map((d, i) => <Cell key={i} fill={GENDER_COLORS[i % GENDER_COLORS.length]} />)}
              </Pie><Tooltip /></PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none"><div className="text-xl font-bold text-[#1F2937]">{data.total}</div><div className="text-[10px] text-[#6B7280]">Total</div></div>
          </div>
          <div className="space-y-1 text-xs mt-2">
            {data.by_gender.map((d, i) => (
              <div key={d.bucket} className="flex justify-between"><span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: GENDER_COLORS[i % GENDER_COLORS.length] }} />{d.bucket}</span><span>{d.count} ({pct(d.count, data.total)}%)</span></div>
            ))}
          </div>
        </div>
        <div className="cc-chart-card">
          <h4 className="font-semibold text-[#1F2937] text-sm mb-2">Patients by Care Team</h4>
          <p className="text-[10px] text-[#6B7280] mb-2">Assigned-physician panels (from RPM enrollments)</p>
          {(data.by_care_team || []).length === 0 ? (
            <div className="text-xs text-[#6B7280] py-8 text-center">No care-team assignments yet.</div>
          ) : (
            <div className="space-y-2 text-xs mt-1">
              {data.by_care_team.map((d, i) => (
                <div key={d.bucket}>
                  <div className="flex justify-between mb-0.5"><span className="flex items-center gap-1.5 truncate"><span className="w-2 h-2 rounded-full shrink-0" style={{ background: TEAM_COLORS[i % TEAM_COLORS.length] }} /><span className="truncate">{d.bucket}</span></span><span className="shrink-0 ml-2">{d.count} ({pct(d.count, data.total)}%)</span></div>
                  <div className="h-1.5 rounded-full bg-[#F1F5F9] overflow-hidden"><div className="h-full rounded-full" style={{ width: `${pct(d.count, data.total)}%`, background: TEAM_COLORS[i % TEAM_COLORS.length] }} /></div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="cc-chart-card">
          <h4 className="font-semibold text-[#1F2937] text-sm mb-2">Disease Burden (Chronic Conditions)</h4>
          <div className="relative">
            <ResponsiveContainer width="100%" height={180}>
              <PieChart><Pie data={data.by_diagnosis.slice(0, 5)} dataKey="count" nameKey="bucket" cx="50%" cy="50%" innerRadius={40} outerRadius={65}>
                {data.by_diagnosis.slice(0, 5).map((d, i) => <Cell key={i} fill={DISEASE_COLORS[i % DISEASE_COLORS.length]} />)}
              </Pie><Tooltip /></PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none"><div className="text-xl font-bold text-[#1F2937]">{data.by_diagnosis.slice(0, 5).reduce((s, d) => s + d.count, 0)}</div><div className="text-[10px] text-[#6B7280]">Top 5</div></div>
          </div>
          <div className="space-y-1 text-xs mt-2">
            {data.by_diagnosis.slice(0, 5).map((d, i) => (
              <div key={d.bucket} className="flex justify-between"><span className="flex items-center gap-1.5 truncate"><span className="w-2 h-2 rounded-full shrink-0" style={{ background: DISEASE_COLORS[i % DISEASE_COLORS.length] }} /><span className="truncate">{d.bucket}</span></span><span className="shrink-0 ml-2">{d.count} ({pct(d.count, data.total)}%)</span></div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="cc-chart-card lg:col-span-2">
          <h4 className="font-semibold text-[#1F2937] text-sm mb-3">High Risk Patient Cohort</h4>
          <div className="overflow-x-auto"><table className="cc-table">
            <thead><tr><th>Patient</th><th>MRN</th><th>Age/G</th><th>Diagnosis</th><th>NEWS2</th><th>RPM Risk</th><th></th></tr></thead>
            <tbody>{explorer.slice(0, 6).map((p) => (
              <tr key={p.id}>
                <td className="font-medium">{p.name}</td>
                <td className="font-mono text-xs"><CopyableId value={p.mrn} kind="MRN" /></td>
                <td>{p.age}/{p.gender?.[0]}</td>
                <td className="text-xs">{p.diagnosis || "—"}</td>
                <td>{p.news2_score ?? "—"}</td>
                <td><span className="px-1.5 py-0.5 rounded text-[10px] uppercase font-bold" style={{ background: `${RISK_COLORS[p.risk_level]}15`, color: RISK_COLORS[p.risk_level] }}>{p.risk_level}</span></td>
                <td><Button size="sm" className="h-6 text-[10px] bg-[#0B7E8C] hover:bg-[#128C97] text-white" onClick={() => window.location.assign(`/clinical/patients/${p.id}`)}>Open</Button></td>
              </tr>
            ))}</tbody>
          </table></div>
        </div>
        <div className="cc-chart-card">
          <h4 className="font-semibold text-[#1F2937] text-sm mb-3">Key Insights</h4>
          <ul className="text-xs text-[#1F2937] space-y-2.5">
            <li className="flex items-start gap-2"><TrendingUp className="w-4 h-4 text-[#F97316] mt-0.5" /><span><strong>{highRisk}</strong> of {data.total} active patients are high-risk or critical ({pct(highRisk, data.total || 1)}%).</span></li>
            <li className="flex items-start gap-2"><HeartPulse className="w-4 h-4 text-[#EF4444] mt-0.5" /><span>Top chronic conditions: {(data.by_diagnosis.slice(0, 2).map((d) => d.bucket).join(", ")) || "—"}.</span></li>
            <li className="flex items-start gap-2"><Layers className="w-4 h-4 text-[#0B7E8C] mt-0.5" /><span><strong>{data.chronic_conditions}</strong> patients ({pct(data.chronic_conditions, data.total || 1)}%) carry a chronic condition.</span></li>
            <li className="flex items-start gap-2"><Users className="w-4 h-4 text-[#8B5CF6] mt-0.5" /><span>Average age <strong>{data.avg_age_years}y</strong> · {pct(female, data.total || 1)}% female across {data.total} patients.</span></li>
          </ul>
        </div>
      </div>
    </div>
  );
}

// =====================================================
// ACTIVE INTERVENTIONS (light)
// =====================================================
export function InterventionsDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], kpis: { by_source: {} } });
  const [source, setSource] = useState("all");
  const [priority, setPriority] = useState("all");

  const load = useCallback(() => {
    const params = {};
    if (source !== "all") params.source = source;
    if (priority !== "all") params.priority = priority;
    api.get("/command/interventions", { params }).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [source, priority]);
  useEffect(() => { load(); }, [load]);
  const k = data.kpis || { by_source: {} };

  return (
    <div data-testid="cc-interventions" className="cc-shell">
      <PageHeader title="Active Interventions" subtitle="Live across RPM, Hotline, Teleconsult, Home Visits, Orders" />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <StatTile icon={ClipboardList} accent="#0B7E8C" label="Open" value={k.open} testId="cc-int-kpi-open" />
        <StatTile icon={AlertTriangle} accent="#EF4444" label="Escalated" value={k.escalated} />
        <StatTile icon={AlertTriangle} accent="#F97316" label="Overdue" value={k.overdue} />
        <StatTile icon={ChevronRight} accent="#10B981" label="Completed Today" value={k.completed_today} />
      </div>

      <div className="cc-card mb-3 flex items-center gap-3 flex-wrap">
        <Filter className="w-4 h-4 text-[#6B7280]" />
        <Select value={source} onValueChange={setSource}>
          <SelectTrigger className="w-44" data-testid="cc-int-source"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources</SelectItem>
            <SelectItem value="rpm">RPM</SelectItem>
            <SelectItem value="hotline">Hotline</SelectItem>
            <SelectItem value="teleconsult">Teleconsult</SelectItem>
            <SelectItem value="home_visit">Home Visit</SelectItem>
            <SelectItem value="order">Order</SelectItem>
          </SelectContent>
        </Select>
        <Select value={priority} onValueChange={setPriority}>
          <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All priorities</SelectItem>
            <SelectItem value="critical">Critical</SelectItem><SelectItem value="urgent">Urgent</SelectItem>
            <SelectItem value="high">High</SelectItem><SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="routine">Routine</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="cc-chart-card p-0 overflow-hidden">
        <div className="overflow-x-auto"><table className="cc-table">
          <thead><tr><th>Source</th><th>Type</th><th>Title</th><th>Patient</th><th>Facility</th><th>Priority</th><th>Status</th><th>Due</th></tr></thead>
          <tbody>
            {data.items.length === 0 && <tr><td colSpan={8} className="p-10 text-center text-[#6B7280]">No interventions match.</td></tr>}
            {data.items.map((i) => (
              <tr key={i.id} data-testid={`cc-int-row-${i.id}`}>
                <td><span className="px-1.5 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider" style={{ background: `${SOURCE_COLORS[i.source]}15`, color: SOURCE_COLORS[i.source] }}>{i.source}</span></td>
                <td className="capitalize">{(i.type || "").replace(/_/g, " ")}</td>
                <td className="font-medium text-[#1F2937]">{i.title}</td>
                <td>{i.patient_id ? <button onClick={() => navigate(`/clinical/patients/${i.patient_id}`)} className="text-left text-[#0B7E8C] hover:underline">{i.patient_name} <span className="font-mono text-xs text-[#94A3B8]">{i.patient_mrn}</span></button> : <span className="text-[#94A3B8]">—</span>}</td>
                <td>{i.facility || "—"}</td>
                <td className="capitalize">{i.priority}</td>
                <td className="capitalize">{(i.status || "").replace(/_/g, " ")}</td>
                <td className="text-xs">{i.due_at ? new Date(i.due_at).toLocaleString() : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>
    </div>
  );
}

// =====================================================
// SLA MANAGER (light)
// =====================================================
export function SlaManager() {
  const [module, setModule] = useState("onboarding");
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/command/sla/${module}`).then((r) => setData(r.data)).catch((e) => { toast.error(formatApiError(e)); setData(null); }); }, [module]);

  return (
    <div data-testid="cc-sla" className="cc-shell">
      <PageHeader title="SLA Manager" subtitle="Workflow funnels, bottlenecks and SLA visibility"
        actions={
          <Select value={module} onValueChange={setModule}>
            <SelectTrigger className="w-52" data-testid="cc-sla-module"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="onboarding">Onboarding</SelectItem>
              <SelectItem value="rpm">RPM</SelectItem>
              <SelectItem value="hotline">Hotline</SelectItem>
              <SelectItem value="teleconsult">Teleconsult</SelectItem>
              <SelectItem value="fleet">Fleet / Home Visits</SelectItem>
            </SelectContent>
          </Select>
        } />
      {!data ? <div className="p-6 text-sm text-[#6B7280]">Loading…</div> : (
        <div className="cc-chart-card">
          <h3 className="font-semibold text-[#1F2937] mb-4 capitalize">{module} Funnel</h3>
          {(() => {
            const max = Math.max(...data.stages.map((s) => s.count || 0), 1);
            return data.stages.map((s, i) => (
              <div key={i} className="cc-funnel-row">
                <span className="cc-funnel-label">{s.stage}</span>
                <div className="cc-funnel-bar"><div className="cc-funnel-fill" style={{ width: `${Math.max(8, (s.count / max) * 100)}%` }} /><div className="cc-funnel-text">{s.count}{s.conversion_pct != null ? ` · ${s.conversion_pct}%` : ""}</div></div>
              </div>
            ));
          })()}
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            {data.rejected != null && <StatTile icon={AlertTriangle} accent="#EF4444" label="Rejected" value={data.rejected} />}
            {data.escalated != null && <StatTile icon={AlertTriangle} accent="#F97316" label="Escalated" value={data.escalated} />}
            {data.delayed != null && <StatTile icon={AlertTriangle} accent="#EF4444" label="Delayed" value={data.delayed} />}
            {data.no_show != null && <StatTile icon={AlertTriangle} accent="#F59E0B" label="No-Show" value={data.no_show} />}
            {data.cancelled != null && <StatTile icon={ClipboardList} accent="#94A3B8" label="Cancelled" value={data.cancelled} />}
            {data.avg_duration_min != null && <StatTile icon={Activity} accent="#0B7E8C" label="Avg Duration (min)" value={data.avg_duration_min} />}
            {data.escalation_rate_pct != null && <StatTile icon={TrendingUp} accent="#F59E0B" label="Escalation %" value={`${data.escalation_rate_pct}%`} />}
            {data.closure_rate_pct != null && <StatTile icon={ChevronRight} accent="#10B981" label="Closure %" value={`${data.closure_rate_pct}%`} />}
            {data.sla_at_risk != null && <StatTile icon={AlertTriangle} accent="#EF4444" label="SLA at risk" value={data.sla_at_risk} />}
          </div>
        </div>
      )}
    </div>
  );
}

// =====================================================
// PATIENT EXPLORER (light)
// =====================================================
export function PatientExplorer() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [facilities, setFacilities] = useState([]);
  const [filters, setFilters] = useState({
    facility_id: "all", risk: params.get("risk") || "all", news2_bucket: "all", gender: "all",
    age_bucket: "all", diagnosis: "",
  });
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);

  const load = useCallback(() => {
    const p = {};
    if (q.trim()) p.q = q.trim();
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") p[k] = v; });
    api.get("/command/patient-explorer", { params: p })
      .then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .catch((e) => toast.error(formatApiError(e)));
  }, [q, filters]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/facilities", { params: { page_size: 50 } }).then((r) => setFacilities(r.data.items || [])).catch(() => {});
  }, []);

  return (
    <div data-testid="cc-explorer" className="cc-shell">
      <PageHeader title="Patient Explorer" subtitle={`${total} matching active patient${total === 1 ? "" : "s"}`} />

      <div className="cc-card mb-3 grid grid-cols-2 md:grid-cols-7 gap-2">
        <div className="relative col-span-2">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-[#94A3B8]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, MRN…" className="pl-9" data-testid="cc-explorer-q" />
        </div>
        <Select value={filters.facility_id} onValueChange={(v) => setFilters({ ...filters, facility_id: v })}>
          <SelectTrigger data-testid="cc-explorer-facility"><SelectValue placeholder="Facility" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All facilities</SelectItem>
            {facilities.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.risk} onValueChange={(v) => setFilters({ ...filters, risk: v })}>
          <SelectTrigger data-testid="cc-explorer-risk"><SelectValue placeholder="Risk" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All risk</SelectItem><SelectItem value="stable">Stable</SelectItem>
            <SelectItem value="moderate">Moderate</SelectItem><SelectItem value="high">High</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.news2_bucket} onValueChange={(v) => setFilters({ ...filters, news2_bucket: v })}>
          <SelectTrigger><SelectValue placeholder="NEWS2" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All NEWS2</SelectItem>
            <SelectItem value="0-2">0-2</SelectItem><SelectItem value="3-4">3-4</SelectItem>
            <SelectItem value="5-6">5-6</SelectItem><SelectItem value="7+">7+</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.age_bucket} onValueChange={(v) => setFilters({ ...filters, age_bucket: v })}>
          <SelectTrigger><SelectValue placeholder="Age" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All ages</SelectItem>
            <SelectItem value="<18">{"<18"}</SelectItem><SelectItem value="18-39">18-39</SelectItem>
            <SelectItem value="40-59">40-59</SelectItem><SelectItem value="60-74">60-74</SelectItem>
            <SelectItem value="75+">75+</SelectItem>
          </SelectContent>
        </Select>
        <Input value={filters.diagnosis} onChange={(e) => setFilters({ ...filters, diagnosis: e.target.value })} placeholder="Diagnosis…" />
      </div>

      <div className="cc-chart-card p-0 overflow-hidden">
        <div className="overflow-x-auto"><table className="cc-table">
          <thead><tr><th>Name</th><th>MRN</th><th>Facility</th><th>Risk</th><th>NEWS2</th><th>RPM</th><th>Team</th><th></th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={8} className="p-10 text-center text-[#6B7280]">No patients match your filters.</td></tr>}
            {items.map((p) => (
              <tr key={p.id} data-testid={`cc-explorer-row-${p.id}`}>
                <td className="font-medium text-[#1F2937]">{p.name} <span className="text-[10px] text-[#94A3B8]">{p.age}y · {p.gender}</span></td>
                <td className="font-mono text-xs text-[#6B7280]"><CopyableId value={p.mrn} kind="MRN" /></td>
                <td className="text-xs text-[#6B7280]">{p.facility}</td>
                <td><span className="px-1.5 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider" style={{ background: `${RISK_COLORS[p.risk_level]}15`, color: RISK_COLORS[p.risk_level] }}>{p.risk_level}</span></td>
                <td>{p.news2_score ?? "—"}</td>
                <td className="capitalize">{(p.rpm_status || "").replace("_", " ")}</td>
                <td className="text-xs">{p.physician || "—"} / {p.nurse || "—"}</td>
                <td className="text-right">
                  <Button size="sm" onClick={() => navigate(`/clinical/patients/${p.id}`)} className="h-7 text-xs bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid={`cc-explorer-open-${p.id}`}>
                    Open <ChevronRight className="w-3 h-3 ml-1" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>
    </div>
  );
}
